// California hazardous waste codes: 22 CCR Division 4.5, Chapter 11,
// Appendix XII. Required on manifests for waste generated in or shipped to
// California, alongside any federal RCRA codes.
// @source https://www.law.cornell.edu/regulations/california/22-CCR-4-5-Chapter-11-app-XII
// @source https://dtsc.ca.gov/wp-content/uploads/sites/31/2019/05/California-State-Hazardous-Waste-Codes.pdf

export type CaWasteCategory =
  | 'inorganics'
  | 'organics'
  | 'sludges'
  | 'miscellaneous'
  | 'restricted';

export interface CaWasteCodeEntry {
  code: string;
  description: string;
  category: CaWasteCategory;
}

export const CA_CATEGORIES: Record<CaWasteCategory, { title: string; blurb: string }> = {
  inorganics: {
    title: 'Inorganics',
    blurb: 'Alkaline and aqueous solutions, surplus inorganic chemicals, asbestos waste, spent catalysts, metal sludges, dusts and other inorganic solids. Metal content and pH drive the code choice.',
  },
  organics: {
    title: 'Organics',
    blurb: 'Solvents by family (halogenated, oxygenated, hydrocarbon), waste oil, pesticide wastes, still bottoms, PCBs, resins, pharmaceutical and biological wastes, and organic liquids and solids.',
  },
  sludges: {
    title: 'Sludges',
    blurb: 'Named process sludges: alum and gypsum, lime, phosphate, sulfur, degreasing, paint, paper and pulp, tetraethyl lead, and unspecified sludge waste.',
  },
  miscellaneous: {
    title: 'Miscellaneous',
    blurb: 'Container wastes, drilling mud, chemical toilet waste, photochemicals, laboratory chemicals, ashes, scrubber and baghouse wastes, contaminated soil, auto shredder and treated wood waste.',
  },
  restricted: {
    title: 'California Restricted Wastes',
    blurb: 'Liquids and solids over specific constituent thresholds (cyanides, arsenic, cadmium, chromium VI, lead, mercury, nickel, selenium, thallium, PCBs, halogenated organics), strong acids and potential dioxin wastes. Restricted from land disposal.',
  },
};

const e = (code: string, description: string, category: CaWasteCategory): CaWasteCodeEntry =>
  ({ code, description, category });

// Full Appendix XII enumeration (80 codes).
export const CA_WASTE_CODES: CaWasteCodeEntry[] = [
  // (1) INORGANICS
  e('121', 'Alkaline solution (pH <= 12.5) with metals (antimony, arsenic, barium, beryllium, cadmium, chromium, cobalt, copper, lead, mercury, molybdenum, nickel, selenium, silver, thallium, vanadium, zinc)', 'inorganics'),
  e('122', 'Alkaline solution without metals (pH > 12.5)', 'inorganics'),
  e('123', 'Unspecified alkaline solution', 'inorganics'),
  e('131', 'Aqueous solution (2 < pH < 12.5) containing reactive anions (azide, bromate, chlorate, cyanide, fluoride, hypochlorite, nitrite, perchlorate, sulfide)', 'inorganics'),
  e('132', 'Aqueous solution with metals at restricted levels', 'inorganics'),
  e('133', 'Aqueous solution with 10% or more total organic residues', 'inorganics'),
  e('134', 'Aqueous solution with less than 10% total organic residues', 'inorganics'),
  e('135', 'Unspecified aqueous solution', 'inorganics'),
  e('141', 'Off-specification, aged, or surplus inorganics', 'inorganics'),
  e('151', 'Asbestos-containing waste', 'inorganics'),
  e('161', 'Fluid-cracking catalyst (FCC) waste', 'inorganics'),
  e('162', 'Other spent catalyst', 'inorganics'),
  e('171', 'Metal sludge', 'inorganics'),
  e('172', 'Metal dust and machining waste', 'inorganics'),
  e('181', 'Other inorganic solid waste', 'inorganics'),
  // (2) ORGANICS
  e('211', 'Halogenated solvents (chloroform, methyl chloride, perchloroethylene, etc.)', 'organics'),
  e('212', 'Oxygenated solvents (acetone, butanol, ethyl acetate, etc.)', 'organics'),
  e('213', 'Hydrocarbon solvents (benzene, hexane, Stoddard, etc.)', 'organics'),
  e('214', 'Unspecified solvent mixture', 'organics'),
  e('221', 'Waste oil and mixed oil', 'organics'),
  e('222', 'Oil/water separation sludge', 'organics'),
  e('223', 'Unspecified oil-containing waste', 'organics'),
  e('231', 'Pesticide rinse water', 'organics'),
  e('232', 'Pesticides and waste from pesticide production', 'organics'),
  e('241', 'Tank bottom waste', 'organics'),
  e('251', 'Still bottoms with halogenated organics', 'organics'),
  e('252', 'Other still bottom waste', 'organics'),
  e('261', 'Polychlorinated biphenyls and PCB-containing materials', 'organics'),
  e('271', 'Organic monomer waste (includes unreacted resins)', 'organics'),
  e('272', 'Polymeric resin waste', 'organics'),
  e('281', 'Adhesives', 'organics'),
  e('291', 'Latex waste', 'organics'),
  e('311', 'Pharmaceutical waste', 'organics'),
  e('321', 'Sewage sludge', 'organics'),
  e('322', 'Biological waste other than sewage sludge', 'organics'),
  e('331', 'Off-specification, aged, or surplus organics', 'organics'),
  e('341', 'Organic liquids (nonsolvents) with halogens', 'organics'),
  e('342', 'Organic liquids with metals', 'organics'),
  e('343', 'Unspecified organic liquid mixture', 'organics'),
  e('351', 'Organic solids with halogens', 'organics'),
  e('352', 'Other organic solids', 'organics'),
  // (3) SLUDGES
  e('411', 'Alum and gypsum sludge', 'sludges'),
  e('421', 'Lime sludge', 'sludges'),
  e('431', 'Phosphate sludge', 'sludges'),
  e('441', 'Sulfur sludge', 'sludges'),
  e('451', 'Degreasing sludge', 'sludges'),
  e('461', 'Paint sludge', 'sludges'),
  e('471', 'Paper sludge/pulp', 'sludges'),
  e('481', 'Tetraethyl lead sludge', 'sludges'),
  e('491', 'Unspecified sludge waste', 'sludges'),
  // (4) MISCELLANEOUS
  e('511', 'Empty pesticide containers 30 gallons or more', 'miscellaneous'),
  e('512', 'Other empty containers 30 gallons or more', 'miscellaneous'),
  e('513', 'Empty containers less than 30 gallons', 'miscellaneous'),
  e('521', 'Drilling mud', 'miscellaneous'),
  e('531', 'Chemical toilet waste', 'miscellaneous'),
  e('541', 'Photochemicals/photoprocessing waste', 'miscellaneous'),
  e('551', 'Laboratory waste chemicals', 'miscellaneous'),
  e('561', 'Detergent and soap', 'miscellaneous'),
  e('571', 'Fly ash, bottom ash, and retort ash', 'miscellaneous'),
  e('581', 'Gas scrubber waste', 'miscellaneous'),
  e('591', 'Baghouse waste', 'miscellaneous'),
  e('611', 'Contaminated soil from site clean-ups', 'miscellaneous'),
  e('612', 'Household waste', 'miscellaneous'),
  e('613', 'Auto shredder waste', 'miscellaneous'),
  e('614', 'Treated wood waste', 'miscellaneous'),
  // (5) CALIFORNIA RESTRICTED WASTES
  e('711', 'Liquids with cyanides >= 1000 mg/l', 'restricted'),
  e('721', 'Liquids with arsenic >= 500 mg/l', 'restricted'),
  e('722', 'Liquids with cadmium >= 100 mg/l', 'restricted'),
  e('723', 'Liquids with chromium (VI) >= 500 mg/l', 'restricted'),
  e('724', 'Liquids with lead >= 500 mg/l', 'restricted'),
  e('725', 'Liquids with mercury >= 20 mg/l', 'restricted'),
  e('726', 'Liquids with nickel >= 134 mg/l', 'restricted'),
  e('727', 'Liquids with selenium >= 100 mg/l', 'restricted'),
  e('728', 'Liquids with thallium >= 130 mg/l', 'restricted'),
  e('731', 'Liquids with polychlorinated biphenyls >= 50 mg/l', 'restricted'),
  e('741', 'Liquids with halogenated organic compounds >= 1000 mg/l', 'restricted'),
  e('751', 'Solids or sludges with halogenated organic compounds >= 1000 mg/kg', 'restricted'),
  e('791', 'Liquids with pH <= 2', 'restricted'),
  e('792', 'Liquids with pH <= 2 with metals', 'restricted'),
  e('801', 'Waste potentially containing dioxins', 'restricted'),
];

const byCode = new Map(CA_WASTE_CODES.map((entry) => [entry.code, entry]));

export function getCaWasteCode(code: string): CaWasteCodeEntry | undefined {
  const trimmed = code.trim();
  if (!/^\d{3}$/.test(trimmed)) return undefined;
  return byCode.get(trimmed);
}

export function getCaCategoryEntries(category: CaWasteCategory): CaWasteCodeEntry[] {
  return CA_WASTE_CODES.filter((entry) => entry.category === category);
}
